import React, { useCallback, useEffect, useState } from "react";
import { orderCardStyles } from "./OrderSummaryCardStyles";
import { Body1, Divider, mergeClasses, Subtitle2, Button } from "@fluentui/react-components";
import {
  HelperUtils,
  useAppStore,
  appInsightsEvents,
  clearActiveCart,
  trackEvent,
} from "../../../common";
import { CartEntry } from "../../Checkout/CartEntries/CartEntry/CartEntry";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { OrderItem, Pricing } from "@dining/api-client";
import { ReusableDialog } from "../../shared/Reorder/ReorderDialog";
import { handleReorder } from "../../../common/reorderUtils";
import { ConfirmationDialog, ConfirmationDialogProps, LoadingScreen } from "../../shared";
import { ApiException } from "@dining/api-client";

export const OrderSummaryCard = () => {
  const styles = orderCardStyles();
  const { reOrder } = useAppStore((state) => state.ordersHistory);
  const { selected, setSelected, values, reorderingEnabled } = useAppStore(
    (state) => state.ordersHistory,
  );
  const [appInsights, context] = useAppStore((state) => [state.appInsights, state.context]);
  const helperUtils = HelperUtils.getInstance();
  const { orderId } = useParams();
  const location = useLocation();
  const { shouldShowReorderButton, order } = location.state || {};
  const [isLoading, setLoading] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<string | JSX.Element>("");
  const [dialogButtonText, setDialogButtonText] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogAction, setDialogAction] = useState<() => void>(() => () => {});
  const navigate = useNavigate();
  const { getCart, value: currentCart, abandonCart } = useAppStore((state) => state.cart);
  const reOrderConsent = false;
  const client = useAppStore((state) => state.client);
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();

  useEffect(() => {
    if (orderId) {
      const order = values?.find((order) => order?.id === orderId);
      if (order) {
        setSelected(order);
      }
    }
  }, [orderId, values, setSelected]);

  const onReorder = useCallback(async () => {
    order?.cafeId !== currentCart?.cafeId && currentCart! ? emptyOtherCart() : handleReorderClick();
  }, [order?.cafeId, currentCart, client]);

  const formatDialogMessage = (items: { name: string }[]) => {
    if (items.length === 0) {
      return "";
    }
    if (items.length === 2) {
      return `${items[0].name} and ${items[1].name}`;
    } else if (items.length > 2) {
      const allButLastItem = items
        .slice(0, -1)
        .map((item) => item.name)
        .join(", ");
      const lastItem = items[items.length - 1].name;
      return `${allButLastItem} and ${lastItem}`;
    }
    return items[0].name;
  };

  const getContentStr = useCallback(
    async (cafeId: string | undefined) => {
      let cafeName: string | undefined;
      let contentStr: string | undefined = "";
      let allItemNames: { name: string }[] = [];
      allItemNames = (order?.items ?? []).map((item: OrderItem) => ({ name: item.name }));
      const itemNames = formatDialogMessage(allItemNames);
      try {
        const response = await client?.getCafeById(cafeId!, undefined);
        cafeName = response?.name;
      } catch (error) {
        if (ApiException.isApiException(error)) {
          const apiException = error as ApiException;
          appInsights.trackException(
            { exception: apiException },
            {
              method: "getCafeById",
              cafeId,
            },
          );
        } else {
          appInsights.trackException(
            { exception: error as Error },
            {
              method: "getCafeById",
              cafeId,
            },
          );
        }
      }

      contentStr = `You currently have an active cart from ${currentCart?.cafeName}. 
                        Do you wish to remove ${
                          currentCart && currentCart?.items?.length > 1
                            ? "those items"
                            : "that item"
                        } 
                        and add ${itemNames} from ${
                          cafeName ? cafeName : ""
                        } to your cart instead?`;
      return contentStr;
    },
    [order?.cafeId, client, currentCart],
  );

  const emptyOtherCart = useCallback(async () => {
    const contentStr = await getContentStr(order?.cafeId);

    setDialogProps({
      content: contentStr,
      open: true,
      title: clearActiveCart,

      confirmAction: async () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.AddItemToCartConfirmed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "OrderHistoryCard",
          },
        );
        await abandonCart();
        handleReorderClick();
        setDialogProps(undefined);
      },
      dismissAction: () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialog.ItemOrderDismissed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "OrderHistoryCard",
          },
        );
        setDialogProps(undefined);
      },
    });
  }, [setDialogProps, abandonCart, appInsights, context, currentCart]);

  const handleReorderClick = () => {
    handleReorder({
      selected: order,
      orderId,
      reOrder,
      reOrderConsent,
      setLoading,
      setDialogOpen,
      setDialogTitle,
      setDialogMessage,
      setDialogAction,
      setDialogButtonText,
      getCart,
      navigate,
    });
  };

  return (
    <>
      {isLoading && (
        <div className={styles.loaderOverlay}>
          <LoadingScreen />
        </div>
      )}
      {selected && (
        <div className={styles.container} data-testid="OrderHistorySummary">
          <main className={styles.main}>
            <div className={styles.subMainContainer}>
              <div className={styles.orderDetailsContainer}>
                <div className={styles.orderDetails}>
                  <div className={styles.orderLeftAlignment}>
                    <div className={styles.order}>
                      <Subtitle2 className={styles.title}>Your Order</Subtitle2>
                      <Body1 className={styles.orderDate}>
                        {helperUtils.getDateFromTimestamp(selected?.timeStamp)}
                      </Body1>
                    </div>
                    <div className={styles.orderDetails}>
                      <div className={styles.order}>
                        <Subtitle2 className={styles.subTitle}>Order Location</Subtitle2>
                        <Body1 className={styles.orderDate}>
                          {selected?.cafeName}:{" "}
                          {[...new Set(selected.items.map((item) => item.stationName))].join(", ")}
                        </Body1>
                      </div>
                    </div>
                  </div>

                  <div className={styles.reorderButtonContainer}>
                    <Subtitle2 className={mergeClasses(styles.title, styles.rightAlignment)}>
                      Order #{selected?.orderNumber}
                    </Subtitle2>
                    <div className={styles.reorderButton}>
                      <Button
                        className={styles.rightAlignmentButton}
                        disabled={!reorderingEnabled || !shouldShowReorderButton}
                        shape="rounded"
                        onClick={onReorder}
                        appearance="primary"
                        type="submit"
                      >
                        <Subtitle2>Reorder</Subtitle2>
                      </Button>
                      {dialogProps && order?.cafeId !== currentCart?.cafeId && currentCart! && (
                        <ConfirmationDialog {...dialogProps} />
                      )}
                    </div>
                  </div>
                </div>
                <Divider appearance="strong" />
              </div>
              <div className={styles.orderSummaryContainer}>
                <Subtitle2 className={styles.title}>Order Summary</Subtitle2>
                <div className={styles.orderSummary}>
                  {selected?.items ? (
                    selected.items.map((item, idx) => {
                      const dietary = helperUtils.getDietaryLabelStr(item);
                      const dietaryAccessible = helperUtils.getDietaryLabelAccessibleStr(item);
                      return (
                        <div key={`${item?.id ?? idx}-container`} className={styles.itemList}>
                          <div key={`${item?.id}-cardContainer`} className={styles.cardEntry}>
                            <CartEntry
                              key={`${item?.id}-card`}
                              customInstructions={item.customInstruction}
                              label={`${item?.name}${dietary ? " " + dietary : ""}`}
                              accessibleLabel={`${item?.name}${dietaryAccessible ? " " + dietaryAccessible : ""}`}
                              subTitle={helperUtils.getSelectedOptionsString(item.options!)}
                              amount={helperUtils.getPricingStr(
                                Pricing.fromJS({
                                  amount: item.amountWithOptions,
                                  currencyUnit: item.pricing?.currencyUnit,
                                }),
                              )}
                              amountClassName={styles.itemAmount}
                            />
                          </div>
                          <Divider key={`${item?.id}-divider`} appearance="strong" />
                        </div>
                      );
                    })
                  ) : (
                    <CartEntry key={"0"} label="Order is empty" />
                  )}
                  {selected?.taxAmount?.amount !== undefined &&
                    selected?.taxAmount?.amount !== null && (
                      <div className={styles.itemList}>
                        <div className={styles.cardEntry}>
                          <CartEntry
                            label="Tax"
                            amount={helperUtils.getPricingStr(
                              Pricing.fromJS({
                                amount: selected?.taxAmount?.amount ?? 0,
                                currencyUnit: selected?.taxAmount?.currencyUnit,
                              }),
                            )}
                            amountClassName={styles.itemAmount}
                          />
                        </div>
                        <Divider appearance="strong" />
                      </div>
                    )}
                  {selected?.totalAmount?.amount && (
                    <div className={styles.itemList}>
                      <div className={styles.cardEntry}>
                        <CartEntry
                          label="Total"
                          amount={helperUtils.getPricingStr(
                            Pricing.fromJS({
                              amount: selected?.totalAmount.amount,
                              currencyUnit: selected?.totalAmount?.currencyUnit,
                            }),
                          )}
                          amountClassName={styles.itemAmount}
                          totalAmountClassName={styles.totalAmount}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      )}
      <ReusableDialog
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogTitle}
        message={dialogMessage}
        dialogButton={dialogButtonText}
        dialogAction={dialogAction}
        cancelButton="Cancel"
        dismissAction={() => setDialogOpen(false)}
      />
    </>
  );
};
